import type {
  DateResolutionInput,
  DealInsightsApi,
  DealInsightsCache,
  DealInsightsCacheKeys,
  DealInsightsChartInputApi,
  DealInsightsCreateJobIdParamsApi,
  DealInsightsTableInputApi,
} from '@openx/types/dealInsights';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dealInsightsDefaultDataApi } from './constants';
import { removeUnrelatedChartsFromParamsString } from './utils/removeUnrelatedChartsFromParamsString';

// Data is updated once per day around 12 UTC so there is no need to update cache more often
const CACHE_TTL = 1000 * 60 * 30; // 30 minutes

export type GetDataFromCacheResult = {
  data: Partial<DealInsightsApi>;
  missingChartKeys: DealInsightsCacheKeys[];
};

export const useDealInsightsCache = (param: {
  params: DealInsightsCreateJobIdParamsApi;
  setFetchDataTriggerParams: Dispatch<SetStateAction<DealInsightsCreateJobIdParamsApi | undefined>>;
  data?: { dealInsights: DealInsightsApi; job_id: string };
  job_id?: string;
  jobCreateParams?: DealInsightsCreateJobIdParamsApi;
  cleanupJob: () => void;
  stopPolling: () => void;
}) => {
  const { params, setFetchDataTriggerParams, data, job_id, jobCreateParams, cleanupJob, stopPolling } = param;

  const [cache, setCache] = useState<DealInsightsCache>({} as DealInsightsCache);

  const jobIdParamsRef = useRef<Record<string, DealInsightsCreateJobIdParamsApi>>({});

  const activeChartKeys = useMemo(() => Object.keys(params.charts) as DealInsightsCacheKeys[], [params.charts]);
  const paramsString = useMemo(() => JSON.stringify(params), [params]);

  const triggerDataFetch = (params: DealInsightsCreateJobIdParamsApi) => {
    setFetchDataTriggerParams(params);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Cache data is checked only when paramsString changes
  useEffect(() => {
    const chartsToFetchConfig = activeChartKeys.reduce(
      (acc, chartKey) => {
        const cacheChartItem = cache[chartKey];
        const paramsStringRelated = removeUnrelatedChartsFromParamsString(paramsString, chartKey);

        if (!cacheChartItem) {
          acc[chartKey] = params.charts[chartKey] as boolean | DealInsightsTableInputApi | DateResolutionInput;
          return acc;
        }

        if (!cacheChartItem[paramsStringRelated]) {
          acc[chartKey] = params.charts[chartKey] as boolean | DealInsightsTableInputApi | DateResolutionInput;
          return acc;
        }

        if (cacheChartItem[paramsStringRelated].timestamp + CACHE_TTL < Date.now()) {
          setCache(prevCache => {
            const { [chartKey]: cachedChartItem, ...restOfCachedCharts } = prevCache;
            delete cachedChartItem[paramsStringRelated];
            return { ...restOfCachedCharts, [chartKey]: { ...cachedChartItem } } as DealInsightsCache;
          });

          acc[chartKey] = params.charts[chartKey] as boolean | DealInsightsTableInputApi | DateResolutionInput;
          return acc;
        }

        return acc;
      },
      {} as Record<keyof DealInsightsChartInputApi, boolean | DealInsightsTableInputApi | DateResolutionInput>
    );

    if (Object.keys(chartsToFetchConfig).length > 0) {
      triggerDataFetch({ ...params, charts: chartsToFetchConfig as DealInsightsChartInputApi });
      return;
    }

    //Data in cache is valid, cleanup active job and stop polling
    cleanupJob();
    stopPolling();
  }, [paramsString, cleanupJob, stopPolling]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Job_id change triggers cache updates
  useEffect(() => {
    if (job_id && jobCreateParams) {
      jobIdParamsRef.current = { ...jobIdParamsRef.current, [job_id]: jobCreateParams };
    }
  }, [job_id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Data change triggers cache updates
  useEffect(() => {
    if (data) {
      const { dealInsights, job_id } = data;
      const { [job_id]: jobCreateParams, ...restJobIdParams } = jobIdParamsRef.current;

      jobIdParamsRef.current = restJobIdParams;

      if (!jobCreateParams) {
        //Just for ts safety, it should never happen
        return;
      }

      const activeChartKeys = Object.keys(jobCreateParams.charts);

      setCache(prevCache => {
        const nextCache = { ...prevCache };

        activeChartKeys.forEach(chartKey => {
          const paramsStringRelated = removeUnrelatedChartsFromParamsString(
            JSON.stringify(jobCreateParams),
            chartKey as keyof DealInsightsChartInputApi
          );
          nextCache[chartKey] = {
            ...nextCache[chartKey],
            [paramsStringRelated]: { timestamp: Date.now(), data: dealInsights[chartKey] },
          };
        });

        return nextCache;
      });

      setFetchDataTriggerParams(undefined);
    }
  }, [data]);

  // Function to get data from cache for specific chart keys
  // Returns data that exists in cache and list of missing keys
  const getDataFromCache = useCallback(
    (chartKeys: DealInsightsCacheKeys[], chartInputs: Partial<DealInsightsChartInputApi>): GetDataFromCacheResult => {
      const result: Partial<DealInsightsApi> = { ...dealInsightsDefaultDataApi };
      const missingChartKeys: DealInsightsCacheKeys[] = [];

      chartKeys.forEach(chartKey => {
        // Build params string for this specific chart key
        const paramsForChart: DealInsightsCreateJobIdParamsApi = {
          ...params,
          charts: { [chartKey]: chartInputs[chartKey] } as DealInsightsChartInputApi,
        };
        const paramsStringForChart = removeUnrelatedChartsFromParamsString(JSON.stringify(paramsForChart), chartKey);

        const cacheChartItem = cache[chartKey];

        if (!cacheChartItem?.[paramsStringForChart]?.data) {
          missingChartKeys.push(chartKey);
          return;
        }

        // Check if cache is expired
        if (cacheChartItem[paramsStringForChart].timestamp + CACHE_TTL < Date.now()) {
          missingChartKeys.push(chartKey);
          return;
        }

        // @ts-expect-error - dynamic key assignment is safe here as chartKey comes from DealInsightsChartInputApi
        result[chartKey] = cacheChartItem[paramsStringForChart].data;
      });

      return { data: result, missingChartKeys };
    },
    [cache, params]
  );

  return { cache, paramsString, activeChartKeys, getDataFromCache, triggerDataFetch };
};
